import React, { useEffect, useMemo, useState } from "react"
import {
  Modal,
  Form,
  Button,
  Spinner,
  Alert
} from "react-bootstrap"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import { toast } from "react-toastify"
import { getFlagTypeOptionsByJobService, postFlag } from "requesters/flagRequester"
import { getBaseUrl } from "../../reducers"

const formatFileSize = size => {
  if (!size && size !== 0) return null
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const FlagCreationModal = ({
  show,
  onHide,
  jobServiceId,
  jobServiceState,
  attachedFile,
  attachedFileName,
  onSuccess
}) => {
  const baseUrl = useSelector(getBaseUrl)
  const [flagTypeMap, setFlagTypeMap] = useState({})
  const [sendToOptions, setSendToOptions] = useState([])
  const [selectedSendTo, setSelectedSendTo] = useState("")
  const [selectedFlagType, setSelectedFlagType] = useState("")
  const [notes, setNotes] = useState("")
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [optionsError, setOptionsError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const currentFlagTypes = useMemo(() => flagTypeMap[selectedSendTo] || [], [flagTypeMap, selectedSendTo])
  const derivedFilename = attachedFileName || attachedFile?.name
  const fileSizeLabel = useMemo(() => formatFileSize(attachedFile?.size), [attachedFile])

  useEffect(() => {
    if (!show) {
      setNotes("")
      setOptionsError(null)
      setSelectedFlagType("")
      setSelectedSendTo("")
      return
    }

    if (!jobServiceId) {
      setOptionsError("Missing job service. Please close and reopen the viewer.")
      return
    }

    setLoadingOptions(true)
    setOptionsError(null)
    getFlagTypeOptionsByJobService(baseUrl, jobServiceId)
      .then(res => {
        if (res.status === 200 && res.response) {
          const sendToKeys = Object.keys(res.response)
          setFlagTypeMap(res.response)
          setSendToOptions(sendToKeys)
          const initialSendTo = sendToKeys[0] || ""
          setSelectedSendTo(initialSendTo)
          const initialFlagTypes = initialSendTo ? res.response[initialSendTo] || [] : []
          setSelectedFlagType(initialFlagTypes[0]?.value || "")
        } else {
          setOptionsError("Unable to load flag options. Please try again.")
        }
      })
      .catch(err => {
        setOptionsError(err.message || "Unable to load flag options.")
      })
      .finally(() => {
        setLoadingOptions(false)
      })
  }, [show, jobServiceId, baseUrl])

  useEffect(() => {
    if (!show) {
      setNotes("")
      setSubmitting(false)
    }
  }, [show])

  const handleChangeSendTo = value => {
    setSelectedSendTo(value)
    const nextFlagTypes = flagTypeMap[value] || []
    setSelectedFlagType(nextFlagTypes[0]?.value || "")
  }

  const handleSubmit = async event => {
    event.preventDefault()
    if (!jobServiceId) {
      toast.error("Cannot create flag: missing job service context.")
      return
    }

    if (!attachedFile) {
      toast.error("Exported PDF not found. Please close the modal and try again.")
      return
    }

    setSubmitting(true)
    const formData = new FormData()
    formData.append("job_service_id", jobServiceId)
    formData.append("flag_type", selectedFlagType)
    formData.append("sent_to", selectedSendTo)
    formData.append("notes", notes)
    if (jobServiceState) {
      formData.append("from_at", jobServiceState)
    }
    formData.append("uploaded_files[]", attachedFile)

    try {
      const result = await postFlag(baseUrl, formData)
      if (result.status >= 200 && result.status < 300) {
        toast.success("Flag created with exported PDF.")
        onSuccess()
      } else {
        throw new Error(result?.error || "Flag creation failed")
      }
    } catch (err) {
      toast.error(err.message || "Unable to create flag.")
    } finally {
      setSubmitting(false)
    }
  }

  const isSubmitDisabled = submitting || !notes.trim() || !selectedFlagType || !selectedSendTo || !attachedFile

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Export PDF &amp; Flag</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {optionsError && (
          <Alert variant="danger" className="mb-3">
            {optionsError}
          </Alert>
        )}

        {loadingOptions ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="flagSendTo" className="mb-4">
              <Form.Label>Send To</Form.Label>
              <Form.Control
                as="select"
                value={selectedSendTo}
                onChange={event => handleChangeSendTo(event.target.value)}
                disabled={!sendToOptions.length}>
                {sendToOptions.map(option => (
                  <option key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="flagType" className="mb-4">
              <Form.Label>Flag Type</Form.Label>
              <Form.Control
                as="select"
                value={selectedFlagType}
                onChange={event => setSelectedFlagType(event.target.value)}
                disabled={!currentFlagTypes.length}>
                {currentFlagTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="flagNotes" className="mb-4">
              <Form.Label>Notes *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={notes}
                onChange={event => setNotes(event.target.value)}
                placeholder="Add context for the drafting or RS team" />
            </Form.Group>

            <div className="border rounded p-3 mb-4 bg-light">
              <h6 className="text-uppercase text-muted">Attached PDF</h6>
              {attachedFile ? (
                <div>
                  <div className="d-flex justify-content-between">
                    <span>{derivedFilename}</span>
                    {fileSizeLabel && <small className="text-muted">{fileSizeLabel}</small>}
                  </div>
                  <small className="text-muted d-block mt-1">
                    This file was generated from your annotations and will be uploaded automatically with the flag.
                  </small>
                </div>
              ) : (
                <small className="text-danger">No exported PDF found. Close the modal and try again.</small>
              )}
            </div>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="mr-2" onClick={onHide} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitDisabled}>
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" className="mr-2" />
                    Creating Flag...
                  </>
                ) : (
                  "Create Flag"
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  )
}

FlagCreationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  jobServiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobServiceState: PropTypes.string,
  attachedFile: PropTypes.instanceOf(File),
  attachedFileName: PropTypes.string,
  onSuccess: PropTypes.func
}

FlagCreationModal.defaultProps = {
  jobServiceId: null,
  jobServiceState: null,
  attachedFile: null,
  attachedFileName: "",
  onSuccess: () => {}
}

export default FlagCreationModal
